import { useState } from "react";
import { Camera, Building2, Package, Users, Sparkles, Plane, ImageIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface Service {
  title: string;
  subtitle: string;
  description: string;
  icon: any;
  keywords: string[];
  category: string;
  imageKey: string;
}

const services: Service[] = [
  {
    title: "Businessporträts & Corporate Headshots",
    subtitle: "Ihre professionelle Visitenkarte",
    description: "Erste Eindrücke zählen. Professionelle Businessporträts zeigen Kompetenz, Vertrauen und Persönlichkeit – genau das, was Ihre Zielgruppe sehen soll. Ob für Website, LinkedIn oder Geschäftsunterlagen: Wir inszenieren Sie so, dass Ihre Expertise sofort sichtbar wird.",
    icon: Users,
    keywords: ["business", "corporate", "portrait", "team"],
    category: "Business",
    imageKey: "business"
  },
  {
    title: "Immobilien- & Architekturfotografie",
    subtitle: "Räume, die verkaufen",
    description: "Eine Immobilie zeigt man nicht – man inszeniert sie. Wir fotografieren Ihre Objekte so, dass potenzielle Käufer:innen oder Mieter:innen sich sofort wohlfühlen und die Qualität auf den ersten Blick erkennen. Mit perfektem Licht, optimalen Perspektiven und cinematischer Bildsprache.",
    icon: Building2,
    keywords: ["immobilien", "real estate", "architektur", "interior"],
    category: "Immobilien",
    imageKey: "immobilien"
  },
  {
    title: "Produkt- & Foodfotografie",
    subtitle: "Produkte, die Appetit machen",
    description: "Ihr Produkt ist einzigartig – und genau so sollte es auch fotografiert werden. Ob E-Commerce, Social Media oder Printanzeigen: Wir setzen Ihre Produkte perfekt in Szene – mit allen Details, die überzeugen.",
    icon: Package,
    keywords: ["produkt", "food", "restaurant", "küche"],
    category: "Produkt",
    imageKey: "produkt"
  },
  {
    title: "Lifestyle- & Teamporträts",
    subtitle: "Echte Menschen, echte Geschichten",
    description: "Ihre Unternehmenskultur verdient mehr als gestellte Stockfotos. Wir zeigen, wer Sie wirklich sind – mit authentischen Bildern Ihres Teams, die Nähe schaffen und Ihre Werte sichtbar machen.",
    icon: Users,
    keywords: ["lifestyle", "team", "people", "kultur"],
    category: "Business",
    imageKey: "lifestyle"
  },
  {
    title: "Kampagnenshootings",
    subtitle: "Visuelle Kampagnen mit Wirkung",
    description: "Ihre Markenbotschaft braucht starke Bilder. Wir entwickeln und realisieren visuelle Kampagnen, die im Gedächtnis bleiben – von der Idee über das Shooting bis zur finalen Bildbearbeitung.",
    icon: Sparkles,
    keywords: ["kampagne", "brand", "luxus", "hotel"],
    category: "Kreativ",
    imageKey: "kampagne"
  },
  {
    title: "Editorial- & Magazinfotografie",
    subtitle: "Geschichten, die man sieht",
    description: "Ob für Magazine, Blogs oder Unternehmensmagazine – wir erzählen Geschichten durch Bilder. Mit journalistischem Gespür, kreativer Bildsprache und höchster technischer Qualität.",
    icon: ImageIcon,
    keywords: ["editorial", "magazin", "story", "reportage"],
    category: "Kreativ",
    imageKey: "editorial"
  },
  {
    title: "Social-Media-Fotografie",
    subtitle: "Content, der stoppt & konvertiert",
    description: "Social Media lebt von Aufmerksamkeit. Wir liefern Ihnen Content, der im Feed nicht untergeht – authentisch, kreativ und genau auf Ihre Zielgruppe abgestimmt.",
    icon: Sparkles,
    keywords: ["social", "media", "content", "instagram"],
    category: "Digital",
    imageKey: "social"
  },
  {
    title: "Drohnenfotografie",
    subtitle: "Perspektiven, die beeindrucken",
    description: "Manchmal muss man die Dinge von oben sehen. Unsere Drohnenfotografie bietet beeindruckende Luftaufnahmen für Immobilien, Events, Unternehmen und Architekturprojekte. Wir fliegen mit EU-Kompetenznachweis (A1/A3) und Drohnen-Haftpflichtversicherung.",
    icon: Plane,
    keywords: ["drohne", "drone", "aerial"],
    category: "Speziell",
    imageKey: "drone"
  },
  {
    title: "Model- & Fashionfotografie",
    subtitle: "Mode, die bewegt",
    description: "Fashion ist Emotion. Wir inszenieren Mode so, dass sie nicht nur gut aussieht, sondern eine Geschichte erzählt. Für Lookbooks, Kampagnen und Modelportfolios.",
    icon: Camera,
    keywords: ["fashion", "mode", "model", "style"],
    category: "Kreativ",
    imageKey: "fashion"
  },
  {
    title: "Event- & Reportagefotografie",
    subtitle: "Momente, die bewegen",
    description: "Events sind vergänglich – Bilder bleiben. Wir halten die Energie, Emotionen und besonderen Momente Ihrer Veranstaltung fest – diskret, professionell und mit Gespür für den richtigen Augenblick.",
    icon: Camera,
    keywords: ["event", "reportage", "dokumentation"],
    category: "Event",
    imageKey: "event"
  },
  {
    title: "Website-Fotografie",
    subtitle: "Bilder, die überzeugen",
    description: "Ihre Website ist Ihr digitales Schaufenster. Wir liefern hochwertige, professionelle Bilder, die Vertrauen schaffen, Ihre Marke stärken und Besucher:innen zu Kund:innen machen.",
    icon: ImageIcon,
    keywords: ["website", "web", "digital"],
    category: "Digital",
    imageKey: "website"
  }
];

const categories = ["Alle", "Business", "Immobilien", "Produkt", "Kreativ", "Digital", "Event", "Speziell"];

export const PhotographyServicesGrid = () => {
  const [selectedCategory, setSelectedCategory] = useState("Alle");
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const filteredServices = selectedCategory === "Alle" 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  return (
    <>
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 justify-center mb-12">
        {categories.map((category) => (
          <Badge
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            className="cursor-pointer px-4 py-2 text-sm hover:scale-105 transition-all"
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </Badge>
        ))}
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredServices.map((service, index) => {
          const ServiceIcon = service.icon;
          
          return (
            <Card
              key={index}
              className="group relative overflow-hidden cursor-pointer border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20 hover:-translate-y-2"
              onClick={() => setSelectedService(service)}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Content */}
              <div className="relative p-6 space-y-4">
                {/* Icon & Category Badge */}
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                    <ServiceIcon className="w-7 h-7 text-primary" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {service.category}
                  </Badge>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold leading-tight group-hover:text-primary transition-colors">
                  {service.title}
                </h3>

                {/* Subtitle */}
                <p className="text-sm text-primary/80 font-medium">
                  {service.subtitle}
                </p>

                {/* Description Preview */}
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {service.description}
                </p>

                {/* Hover indicator */}
                <div className="flex items-center gap-2 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Mehr erfahren</span>
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Service Details Dialog */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-2xl">
          {selectedService && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                    <selectedService.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-2xl mb-1">
                      {selectedService.title}
                    </DialogTitle>
                    <Badge variant="secondary">{selectedService.category}</Badge>
                  </div>
                </div>
              </DialogHeader>
              
              <DialogDescription className="space-y-4">
                <p className="text-lg font-semibold text-primary">
                  {selectedService.subtitle}
                </p>
                <p className="text-base text-foreground leading-relaxed">
                  {selectedService.description}
                </p>
                <div className="flex flex-wrap gap-2 pt-4">
                  {selectedService.keywords.map((keyword, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </DialogDescription>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
